import React, {useState, useEffect} from 'react'
import GenericTable from '../GenericTable/table'
import {SubscriptionData, fetchSubscriptionData} from './helper'
import Swal from 'sweetalert2'
import {updatePaidStatus, updateSubscriptionEndDate} from '../../../api'
import SkeletonCard from '../Auth/Components/SkeletonCard'
import {Tooltip} from 'react-tooltip'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {StaticDatePicker} from '@mui/x-date-pickers/StaticDatePicker'

type SortConfig = {key: string; direction: 'asc' | 'desc' | null}

const UpdatePlan = () => {
  const [columns, setColumns] = useState<string[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [downloadData, setDownloadData] = useState<Record<string, any>[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentPageData, setCurrentPageData] = useState<Record<string, any>[]>([])
  const [totalPages, setTotalPages] = useState<number>(1)
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: '', direction: null})
  const [openCalendarRowId, setOpenCalendarRowId] = useState<string | null>(null)
  const [tempDates, setTempDates] = useState<Record<string, Date | null>>({})
  const [isDownloading, setIsDownloading] = useState(false)
  const itemsPerPage = 10

  // Map data to match the columns
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const data = await fetchSubscriptionData()
        console.log('Fetched subscription data:', data)
        setColumns(SubscriptionData.columns)
        setData(mapDataToColumns(data, SubscriptionData.columns))
        setDownloadData(mapDataToColumns(data, SubscriptionData.columns))
        setTotalPages(Math.ceil(data.length / itemsPerPage))
      } catch (error) {
        console.error('Error setting subscription data:', error)
      } finally {
        setLoading(false) // Stop loading after fetching data or encountering an error
      }
    }

    fetchData()
  }, [])

  const sortData = (data: Record<string, any>[], column: string) => {
    const sortableData = [...data]
    sortableData.sort((a, b) => {
      const aValue = a[column]
      const bValue = b[column]
      if (column === 'Paid Status') {
        const aStatus = aValue === 'Paid' || aValue === true ? 1 : 0
        const bStatus = bValue === 'Paid' || bValue === true ? 1 : 0
        return sortConfig.direction === 'asc' ? aStatus - bStatus : bStatus - aStatus
      }

      const aDate = Date.parse(aValue)
      const bDate = Date.parse(bValue)
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate
      }

      if (!isNaN(aValue) && !isNaN(bValue)) {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return 0
    })

    return sortableData
  }

  const handleSort = (column: string) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig.key === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
      return {key: column, direction}
    })
  }

  useEffect(() => {
    if (sortConfig.key) {
      const sortedData = sortData(data, sortConfig.key)
      setData(sortedData)
      setDownloadData(sortedData)
    }
  }, [sortConfig])

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  // Map data from the original keys to columns (handling spaces in column names)
  const mapDataToColumns = (
    data: Record<string, any>[],
    columns: string[]
  ): Record<string, any>[] => {
    if (!data || !Array.isArray(data)) {
      console.warn('mapDataToColumns received invalid data:', data)
      return []
    }

    return data.map((row) => {
      const mappedRow: Record<string, any> = {}
      columns.forEach((col) => {
        const key = col.replace(/\s+/g, '')
        mappedRow[col] = row[key] || ''
      })
      return mappedRow
    })
  }

  const handleStatusChange = (farmId: string, newStatus: string) => {
    const selectedRow = data.find((row) => row['Farm Id'] === farmId)

    if (!selectedRow) {
      Swal.fire('Error', 'Farm not found.', 'error')
      return
    }

    Swal.fire({
      title: `Are you sure you want to change the status?`,
      // text: `Are you sure you want to update the status to ${newStatus} for this Farm?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        setData((prevData) => {
          const updatedData = prevData.map((row) =>
            row['Farm Id'] === farmId ? {...row, 'Paid Status': newStatus} : row
          )
          return updatedData
        })

        const isPaid = newStatus === 'Paid'
        updatePaidStatus({farm_id: farmId, new_is_paid_status: isPaid})
          .then(() => {
            Swal.fire('Updated!', `Status has been changed to <b>${newStatus}</b>.`, 'success')
          })
          .catch((error) => {
            const errorMessage =
              error.response?.data?.message || 'There was an issue updating the status.'
            Swal.fire('Error', errorMessage, 'error')
          })
      }
    })
  }

  useEffect(() => {
    const filteredData = data.filter((row) =>
      Object.values(row).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
    )
    setDownloadData(filteredData)
    // Calculate total pages and set the current page data
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage))
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    setCurrentPageData(filteredData.slice(startIndex, endIndex))
  }, [data, currentPage, searchTerm])

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      // Check if click is outside the calendar popup and not on the calendar icon
      if (
        openCalendarRowId &&
        !target.closest('[data-calendar-popup]') &&
        !target.closest('.bi-calendar3') &&
        !target.closest('[data-calendar-icon]')
      ) {
        setOpenCalendarRowId(null)
        setTempDates((prev) => {
          const newDates = {...prev}
          if (openCalendarRowId) {
            delete newDates[openCalendarRowId]
          }
          return newDates
        })
      }
    }

    if (openCalendarRowId) {
      // Use a small delay to avoid closing immediately when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [openCalendarRowId])

  const handleCalendarIconClick = (farmId: string, currentDate: string) => {
    // Toggle calendar - if already open for this row, close it
    if (openCalendarRowId === farmId) {
      setOpenCalendarRowId(null)
      setTempDates((prev) => {
        const newDates = {...prev}
        delete newDates[farmId]
        return newDates
      })
      return
    }

    // Try to parse the current date
    // The date format in table is "DD-MMM-YYYY" (e.g., "01-Jun-2025")
    let parsedDate: Date | null = null
    if (currentDate) {
      const dateStr = currentDate.trim()

      // Try parsing DD-MMM-YYYY format (e.g., "01-Jun-2025")
      const ddmmyyyyMatch = dateStr.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/)
      if (ddmmyyyyMatch) {
        const [, day, month, year] = ddmmyyyyMatch
        const monthMap: Record<string, string> = {
          Jan: '01',
          Feb: '02',
          Mar: '03',
          Apr: '04',
          May: '05',
          Jun: '06',
          Jul: '07',
          Aug: '08',
          Sep: '09',
          Oct: '10',
          Nov: '11',
          Dec: '12',
        }
        const monthNum = monthMap[month]
        if (monthNum) {
          parsedDate = new Date(`${year}-${monthNum}-${day}`)
        }
      }

      // If parsing failed, try Date.parse
      if (!parsedDate || isNaN(parsedDate.getTime())) {
        const dateValue = Date.parse(currentDate)
        if (!isNaN(dateValue)) {
          parsedDate = new Date(dateValue)
        }
      }

      // Try YYYY-MM-DD format as fallback
      if ((!parsedDate || isNaN(parsedDate.getTime())) && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        parsedDate = new Date(dateStr)
      }
    }

    setOpenCalendarRowId(farmId)
    setTempDates((prev) => ({
      ...prev,
      [farmId]: parsedDate,
    }))
  }

  const handleApplyDate = async (farmId: string) => {
    const tempDate = tempDates[farmId]
    if (tempDate) {
      // Format date as YYYY-MM-DD
      const year = tempDate.getFullYear()
      const month = String(tempDate.getMonth() + 1).padStart(2, '0')
      const day = String(tempDate.getDate()).padStart(2, '0')
      const formattedDate = `${year}-${month}-${day}`

      try {
        // Call the update API
        await updateSubscriptionEndDate({
          farm_id: farmId,
          end_date: formattedDate,
        })

        // If update succeeds, refresh the data
        setLoading(true)
        const updatedData = await fetchSubscriptionData()
        setData(mapDataToColumns(updatedData, SubscriptionData.columns))
        setTotalPages(Math.ceil(updatedData.length / itemsPerPage))

        Swal.fire('Success!', 'End date has been updated successfully.', 'success')

        // Close the calendar
        setOpenCalendarRowId(null)
        setTempDates((prev) => {
          const newDates = {...prev}
          delete newDates[farmId]
          return newDates
        })
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || 'There was an issue updating the end date.'
        Swal.fire('Error', errorMessage, 'error')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleCancelDate = (farmId: string) => {
    setOpenCalendarRowId(null)
    setTempDates((prev) => {
      const newDates = {...prev}
      delete newDates[farmId]
      return newDates
    })
  }

  const downloadCSV = () => {
    if (!downloadData || downloadData.length === 0) return

    setIsDownloading(true)

    try {
      // Extract headers from first object
      const headers = Object.keys(downloadData[0])

      // Convert rows
      const rows = downloadData.map((row: any) =>
        headers.map((header) => {
          const value = row[header] ?? ''
          // Escape commas, quotes, new lines
          return `"${String(value).replace(/"/g, '""')}"`
        })
      )

      // Build CSV content
      const csvContent = [
        headers.join(','), // header row
        ...rows.map((r) => r.join(',')),
      ].join('\n')

      // Create & download file
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = 'farm_data.csv'
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div>
      {loading ? (
        <SkeletonCard />
      ) : (
        <>
          <div className='relative'>
            <button
              type='button'
              className='btn'
              disabled={isDownloading}
              style={{
                width: '10%',
                position: 'absolute',
                right: '3rem',
                top: '8rem',
                zIndex: 20,
                backgroundColor: '#6D9300',
                color: '#fff',
                cursor: isDownloading ? 'not-allowed' : 'pointer',
              }}
              onClick={downloadCSV}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </button>
            <GenericTable
              columns={columns}
              handleNextClick={handleNextClick}
              handlePriviousClick={handlePreviousClick}
              data={currentPageData}
              handleSort={handleSort}
              sortConfig={sortConfig}
              searchTerm={searchTerm}
              onSearch={(term) => {
                setSearchTerm(term)
                setCurrentPage(1)
              }}
              renderCell={(column, row, rowIndex) => {
                if (column === 'Paid Status') {
                  return (
                    <select
                      className='form-select form-select-solid'
                      style={{minWidth: '150px'}}
                      data-control='select2'
                      data-placeholder='Select an option'
                      value={
                        row['Paid Status'] === 'Paid' || row['Paid Status'] === true
                          ? 'Paid'
                          : 'Not Paid'
                      }
                      onChange={(e) => handleStatusChange(row['Farm Id'], e.target.value)}
                    >
                      <option value='Paid'>Paid</option>
                      <option value='Not Paid'>Not Paid</option>
                    </select>
                  )
                }
                if (column === 'Center') {
                  const centerUrl = row[column]
                  return centerUrl ? (
                    <a href={centerUrl} target='_blank' rel='noopener noreferrer'>
                      {centerUrl}
                    </a>
                  ) : (
                    <span>No URL available</span>
                  )
                }
                if (column === 'Farm Name') {
                  const text = row[column] || ''
                  const maxLength = 20
                  const truncatedText =
                    text.length > maxLength ? text.slice(0, maxLength) + '...' : text

                  return (
                    <>
                      <span data-tooltip-id='comment-tooltip' data-tooltip-content={text}>
                        {truncatedText}
                      </span>
                      <Tooltip
                        id='comment-tooltip'
                        place='bottom' // Tooltip position: top, bottom, left, right
                        style={{
                          backgroundColor: '#6D9300',
                          color: '#fff',
                          padding: '4px',
                          borderRadius: '6px',
                        }}
                      />
                    </>
                  )
                }
                if (column === 'Days Left') {
                  const daysLeftStr = row[column] || '0'
                  const daysLeft = parseInt(daysLeftStr.toString().replace(/\D/g, '')) || 0
                  let color = ''

                  if (daysLeft <= 0) {
                    color = '#000000' // expired - black
                  } else if (daysLeft <= 7) {
                    color = '#dc3545' // critical - red
                  } else if (daysLeft <= 30) {
                    color = '#fd7e14' // urgent - orange
                  } else if (daysLeft <= 60) {
                    color = '#ffc107' // expiring soon - yellow
                  } else {
                    color = '#28a745' // active - green
                  }

                  return <span style={{color: color, fontWeight: '500'}}>{daysLeftStr}</span>
                }
                if (column === 'Status') {
                  const status = row[column] || ''
                  const statusLower = status.toString().toLowerCase()
                  let color = ''
                  let bgColor = ''

                  if (statusLower.includes('expired')) {
                    color = '#000000'
                    bgColor = 'rgba(0, 0, 0, 0.1)'
                  } else if (statusLower.includes('critical')) {
                    color = '#dc3545'
                    bgColor = 'rgba(220, 53, 69, 0.1)'
                  } else if (statusLower.includes('urgent')) {
                    color = '#fd7e14'
                    bgColor = 'rgba(253, 126, 20, 0.1)'
                  } else if (statusLower.includes('expiring soon')) {
                    color = '#ffc107'
                    bgColor = 'rgba(255, 193, 7, 0.1)'
                  } else if (statusLower.includes('active')) {
                    color = '#28a745'
                    bgColor = 'rgba(40, 167, 69, 0.1)'
                  }

                  return (
                    <span
                      style={{
                        color: color,
                        border: '1px solid ' + color,
                        backgroundColor: bgColor,
                        fontWeight: '500',
                        padding: '4px 12px',
                        borderRadius: '30px',
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {status}
                    </span>
                  )
                }
                if (column === 'End Date') {
                  const endDate = row[column] || ''
                  const farmId = row['Farm Id']
                  const isCalendarOpen = openCalendarRowId === farmId

                  return (
                    <div style={{position: 'relative'}}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <i
                          data-calendar-icon
                          className='bi bi-calendar3'
                          style={{cursor: 'pointer', color: '#6D9300', fontSize: '16px'}}
                          onClick={() => handleCalendarIconClick(farmId, endDate)}
                        />
                        {endDate}
                      </div>
                      {isCalendarOpen && (
                        <div
                          data-calendar-popup
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 1000,
                            backgroundColor: 'white',
                            padding: '3px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            marginTop: '5px',
                          }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <div>
                              <style>
                                {`
                                .MuiPickersDay-root.Mui-selected {
                                  background-color: #6D9300 !important;
                                  color: white !important;
                                }
                                .MuiPickersDay-root.Mui-selected:hover {
                                  background-color: #5a7a00 !important;
                                }
                                .MuiPickersDay-root.Mui-selected:focus {
                                  background-color: #6D9300 !important;
                                }
                                .MuiPickersDay-root:not(.Mui-disabled):hover {
                                  background-color: rgba(109, 147, 0, 0.1) !important;
                                }
                                .MuiPickersDay-root.Mui-selected.Mui-focusVisible {
                                  background-color: #6D9300 !important;
                                }
                              `}
                              </style>
                              <StaticDatePicker
                                value={tempDates[farmId] || null}
                                onChange={(newValue) =>
                                  setTempDates((prev) => ({
                                    ...prev,
                                    [farmId]: newValue,
                                  }))
                                }
                                displayStaticWrapperAs='desktop'
                                slotProps={{
                                  actionBar: {
                                    actions: [],
                                  },
                                }}
                              />
                            </div>
                          </LocalizationProvider>
                          <div
                            style={{
                              display: 'flex',
                              gap: '10px',
                              justifyContent: 'flex-end',
                              padding: '3px',
                              paddingTop: '8px',
                              borderTop: '1px solid #e0e0e0',
                              position: 'absolute',
                              width: '98%',
                              bottom: 5,
                            }}
                          >
                            <button
                              type='button'
                              className='btn btn-sm btn-secondary'
                              onClick={() => handleCancelDate(farmId)}
                            >
                              Cancel
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm'
                              onClick={() => handleApplyDate(farmId)}
                              style={{
                                backgroundColor: '#6D9300',
                                borderColor: '#6D9300',
                                color: '#fff',
                              }}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                }
                return row[column]
              }}
              conditionalClass='card card-flush mb-6 mb-xl-9 mt-5'
            />
          </div>
          <ul className='pagination justify-content-center' style={{margin: '15px 0'}}>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handlePreviousClick) handlePreviousClick()
                }}
                disabled={currentPage === 1}
                aria-label='Previous'
              >
                &laquo; Previous
              </button>
            </li>

            <li className='page-item active'>
              <div className='d-flex align-items-center'>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {currentPage}
                </button>
                <span className='mx-2'>of</span>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {totalPages}
                </button>
              </div>
            </li>

            <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handleNextClick) handleNextClick()
                }}
                disabled={currentPage >= totalPages}
                aria-label='Next'
              >
                Next &raquo;
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  )
}

export {UpdatePlan}
